import React, {useEffect, useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import {CSVLink} from 'react-csv';
import {PageHeader} from "../components/layout/PageHeader";
import {Button, Col, Form, InputNumber, Pagination, Row, Select, Spin, DatePicker, Table} from "antd";
import {useHistory} from "react-router-dom";
import {createBillingSetting, getBillingSettings, useBillingSetting, useBillingSettings} from "../hooks/billing";
import {getReportByCompany} from "../hooks/report";
import {BillingSettingCreateDto, BillingSettingDto, BillingSettingListItemDto, ReportCompanyDto} from "../generated-api";
import {getClassifierCountries, useCountriesClassifier} from "../hooks/classifier";
import {PlusCircle} from "react-feather";
import {DEFAULT_PAGE_SIZE} from "../constants";
import {BackButton} from "../components/layout/BackButton";
import {dateFormat} from "../utils/render";
import {toISO} from "../utils/datetime";
import moment from "moment";

const { RangePicker } = DatePicker;
const pageSize = DEFAULT_PAGE_SIZE;

export class ReportCompanyFormattedDto {
	companyId?: string;
	companyName: string;
	reader2Count?: number;
    reader2CountPeriod?: number;
    certificatesCount?: number;
	filesInCertificatesAutoCount?: number;
    filesInCertificatesManualCount?: number;
    filesResultsCount?: number;
	sizeUploadManualMb: string;
    sizeCertificatesUploadAutoMb: string;
    sizeResultsUploadAutoMb: string;
    sizeDownloadManualMb?: string;
    sizeDownloadAutoMb?: string;
    watermarkDownloadCount?: number 

	constructor(obj: ReportCompanyDto) {
        this.companyId = obj.companyId;
        this.companyName = obj.companyName;
        this.reader2Count = obj.reader2Count;
        this.reader2CountPeriod = obj.reader2CountPeriod;
        this.certificatesCount = obj.certificatesCount;
        this.filesInCertificatesAutoCount = obj.filesInCertificatesAutoCount;
        this.filesInCertificatesManualCount = obj.filesInCertificatesManualCount;
        this.filesResultsCount = obj.filesResultsCount;
        this.sizeUploadManualMb = obj.sizeUploadManualMb!!.toLocaleString('de');
        this.sizeCertificatesUploadAutoMb = obj.sizeCertificatesUploadAutoMb!!.toLocaleString('de');
        this.sizeResultsUploadAutoMb = obj.sizeResultsUploadAutoMb!!.toLocaleString('de');
        this.sizeDownloadManualMb = obj.sizeDownloadManualMb!!.toLocaleString('de');
        this.sizeDownloadAutoMb = obj.sizeDownloadAutoMb!!.toLocaleString('de');
        this.watermarkDownloadCount = obj.watermarkDownloadCount;
	}
}

export function BillingSettingsPage() {
    const {t} = useTranslation();
    const history = useHistory();
    const countriesResponse = useCountriesClassifier();
    const countries = countriesResponse.data || [];
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let reportParmas = {reportDateRange: [
            moment().utc().subtract(1,'months').startOf('month'),
            moment().utc().subtract(1,'months').endOf('month')
        ]};
        reportForm.setFieldsValue(reportParmas);
        getReportData(reportParmas);
        getBillingSettings().then().catch(console.error);
        if (!countries.length) {
            getClassifierCountries().then().catch(console.error);
        }
    }, [true]);
    
    const billingSettingResponse = useBillingSetting();
    const billingSettingsResponse = useBillingSettings();
    const billingSettings = billingSettingsResponse.data || [];
    const [page, setPage] = useState(1);
    const [addFormCollapsed, setAddFormCollapsed] = useState(true);
    const [form] = Form.useForm();
    const [reportForm] = Form.useForm();

    const [reportData, setReportData] = useState<ReportCompanyFormattedDto[]>([]);
    const [reportFileName, setReportFileName] = useState<string>("");

    const csvLink = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null)

    const validateMessages = {
        required: t('error.required'),
        types: {
            number: t('error.type.number')
        }
    };

    const onFinish = (values: BillingSettingCreateDto): void => {
        createBillingSetting(values).then((billingSetting: BillingSettingDto) => {
            history.push('/billingSettings/' + billingSetting.id);
        }).catch(console.error);
    };

    const onFinishGetBillingCsvReport = (values: any): void => {
        const { reportDateRange } = values;
        getReportByCompany(toISO(reportDateRange[0]), toISO(reportDateRange[1]))
            .then(report => {
                setReportFileName(`report.${report.from}-${report.to}`);
                setReportData(report.companies.map(r => {
                    return new ReportCompanyFormattedDto(r);
                }));
                csvLink!.current!.link.click();
            }).catch(console.error);
    }

    const getReportData = (values: any): void => {
        const { reportDateRange } = values;
        setLoading(true)
        getReportByCompany(toISO(reportDateRange[0]), toISO(reportDateRange[1]))
        .then(report => {
            setReportData(report.companies.map(r => {
                return new ReportCompanyFormattedDto(r);
            }));
        })
        .catch((console.error))
        .finally(() => setLoading(false));
    }

    const processed = [];
    for (let i = (page - 1) * pageSize; i < page * pageSize; i++) {
        billingSettings[i] && processed.push(billingSettings[i]);
    }

    return (
        <Spin spinning={billingSettingsResponse.loading}>
            <PageHeader/>
            <div className={'top-block'}>
                <div className={'flex-row'}>
                    <h1 className={'h1 flex-auto'}><BackButton/>{t('billingSettings.title')}</h1>
                    <a href={'/billingSettings/add'}
                       className={'title-icon-button ' + (addFormCollapsed ? '' : 'toggle')}
                       onClick={event => {
                           event.preventDefault();
                           setAddFormCollapsed(!addFormCollapsed);
                           form.resetFields();
                       }}>
                        <PlusCircle className={addFormCollapsed ? '' : 'rotate45'}/>
                    </a>
                </div>
                <Spin spinning={billingSettingResponse.loading}>
                    <Form form={form} name="billingSetting" onFinish={onFinish} validateMessages={validateMessages}
                          autoComplete={'off'} className={addFormCollapsed ? 'collapsed' : 'expanded'}
                          validateTrigger={'onSubmit'}>
                        <Row className={'row-offset'}>
                            <Col span={24} md={12} xl={8}>
                                <Form.Item name={'countryId'} rules={[{required: true}]}>
                                    <Select allowClear showSearch style={{width: '100%'}}
                                            placeholder={t('billingSetting.country')}
                                            filterOption={(input, option): any =>
                                                String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                            }>
                                        {countries.map((country: any) =>
                                            <Select.Option key={country.id}
                                                           value={country.id}>{country.name}</Select.Option>
                                        )}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24} md={12} xl={8}>
                                <Form.Item name={'price'} rules={[{required: true}, {type: 'number'}]}>
                                    <InputNumber placeholder={t('billingSetting.price')}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item className={'no-margin'}>
                            <Button type="primary" className={'white-background text-default w-100'} htmlType="submit">
                                {t('billingSettings.add')}
                            </Button>
                        </Form.Item>
                    </Form>
                    <div className={'top-block pb-2"'}> 
                        <Form
                            name="billingReportByCompany"
                            form={reportForm}
                            onFinish={onFinishGetBillingCsvReport}
                        >
                            <Form.Item label={t('billingSettings.csvDateRange')} name={'reportDateRange'}>  
                                <RangePicker  
                                    className="ant-daterange-input"
                                    format={dateFormat}
                                    onChange={_ => {
                                        const values = {...reportForm.getFieldsValue()};
                                        reportForm.setFieldsValue(values);
                                        getReportData(values);
                                    }}
                                />
                            </Form.Item>
                            <Form.Item className={'no-margin text-right'}>
                                <Button type="primary" className={'accent7-background text-white w-100'} htmlType="submit">
                                    {t('billingSettings.csvReport')}
                                </Button>
                            </Form.Item>
                        </Form>
                        <Table loading={loading} dataSource={reportData} size="small" pagination={false} columns={[
                        {
                            title: 'Company', 
                            dataIndex: 'companyName',
                            key: 'companyName',
                        },
                        {
                            title: 'TraCertDoc count', 
                            dataIndex: 'certificatesCount',
                            key: 'certificatesCount',
                        },
                        {
                            title: 'Watermark count', 
                            dataIndex: 'watermarkDownloadCount',
                            key: 'watermarkDownloadCount',
                        },
                        {
                            title: 'ATC files certificates count', 
                            dataIndex: 'filesInCertificatesAutoCount',
                            key: 'filesInCertificatesAutoCount',
                        },
                        {
                            title: 'Upload files certificates count', 
                            dataIndex: 'filesInCertificatesManualCount',
                            key: 'filesInCertificatesManualCount',
                        },
                        {
                            title: 'ATC files results count', 
                            dataIndex: 'filesResultsCount',
                            key: 'filesResultsCount',
                        },
                        {
                            title: 'Upload manual size in MB', 
                            dataIndex: 'sizeUploadManualMb',
                            key: 'sizeUploadManualMb',
                        },
                        {
                            title: 'ATC results in MB', 
                            dataIndex: 'sizeResultsUploadAutoMb',
                            key: 'sizeResultsUploadAutoMb',
                        },
                        {
                            title: 'ATC in MB', 
                            dataIndex: 'sizeCertificatesUploadAutoMb',
                            key: 'sizeCertificatesUploadAutoMb',
                        },
                        {
                            title: 'ATR in MB', 
                            dataIndex: 'sizeDownloadAutoMb',
                            key: 'sizeDownloadAutoMb',
                        },
                        {
                            title: 'Download manual size in MB', 
                            dataIndex: 'sizeDownloadManualMb',
                            key: 'sizeDownloadManualMb',
                        },
                        {
                            title: 'Reader2 quantity total', 
                            dataIndex: 'reader2Count',
                            key: 'reader2Count',
                        },
                        {
                            title: 'Reader2 quantity period', 
                            dataIndex: 'reader2CountPeriod',
                            key: 'reader2CountPeriod',
                        },
                        ]}/>
                        <CSVLink
                            headers={[
                                
                                { label: "Company Name", key: "companyName" },
                                { label: "TraCertDoc count", key: "certificatesCount" },
                                { label: "Watermark count", key: "watermarkDownloadCount" },
                                { label: "ATC files certificates count", key: "filesInCertificatesAutoCount" },
                                { label: "Upload files certificates count", key: "filesInCertificatesManualCount" },
                                { label: "ATC files results count", key: "filesResultsCount" },
                                { label: "Upload manual size in MB", key: "sizeUploadManualMb" },
                                { label: "ATC results in MB", key: "sizeResultsUploadAutoMb" },
                                { label: "ATC in MB", key: "sizeCertificatesUploadAutoMb" },
                                { label: "ATR in MB", key: "sizeDownloadAutoMb" },
                                { label: "Download manual size in MB", key: "sizeDownloadManualMb" },
                                { label: "Reader2 quantity total", key: "reader2Count" },
                                { label: "Reader2 quantity period", key: "reader2CountPeriod" },
                              ]}
                            data={reportData}
                            filename={`${reportFileName}.csv`}
                            className='hidden'
                            ref={csvLink}
                            separator={";"}
                            target='_blank'
                        />
                    </div>
                </Spin>
            </div>
            <div className={'subpage-content'}>
                {processed.map((billingSetting: BillingSettingListItemDto, index: number) =>
                    <div key={index} className={'card accent7-background'}>
                        <a href={'/billingSettings/' + billingSetting.id} onClick={event => {
                            event.preventDefault();
                            history.push('/billingSettings/' + billingSetting.id);
                        }}>
                            <div className={'card-header flex-row no-border'}>
                                {(billingSetting.country || {}).name}
                            </div>
                        </a>
                    </div>
                )}
                {!processed.length && <div className={'no-data'}>{t('general.noData')}</div>}
                {billingSettings.length > pageSize &&
                <Pagination onChange={setPage} pageSize={pageSize} total={billingSettings.length} current={page}
                            defaultCurrent={page}/>
                }
            </div>
        </Spin>
    )
}
